import Component from "@glimmer/component";
import { modifier } from "ember-modifier";
import { action } from "@ember/object";
import { htmlSafe } from "@ember/template";
import { tracked } from "@glimmer/tracking";
import { task } from "ember-concurrency";
import { LinkTo } from "@ember/routing";
import { on } from "@ember/modifier";
import { fn, get } from "@ember/helper";
import { t } from "ember-intl";
import { arg, forbidExtraArgs } from "glesys-controlpanel/utils/arg";
import { func, instanceOf } from "prop-types";
import DataLoader from "glesys-controlpanel/components/data-loader";
import Dropdown from "glesys-controlpanel/components/dropdown";
import DropdownItem from "glesys-controlpanel/components/dropdown/item";
import DropdownDestructiveItem from "glesys-controlpanel/components/dropdown/destructive-item";
import IconsMoreHorizontal from "glesys-controlpanel/components/icons/more-horizontal";
import IconsPlus from "glesys-controlpanel/components/icons/plus";
import canManage from "glesys-controlpanel/helpers/can-manage";
import modal from "glesys-controlpanel/helpers/modal";
import OverlayLoadingSpinner from "glesys-controlpanel/components/overlay-loading-spinner";
import StatusIndicator from "glesys-controlpanel/components/status-indicator";
import PrivateNetwork from "glesys-controlpanel/models/private-network";
import styles from "./styles.module.css";

const PACKET_SPEED = 42;
const PACKET_SPACING = 60;

const trackLayout = modifier((element, [onLayout]) => {
  let measure = () => onLayout(element);

  let resized = new ResizeObserver(measure);
  let nodesChanged = new MutationObserver(measure);
  let nodeIdsChanged = new MutationObserver(measure);

  resized.observe(element);
  nodesChanged.observe(element, { childList: true });
  nodeIdsChanged.observe(element, { subtree: true, attributeFilter: ["data-topology-node"] });
  measure();

  return () => {
    resized.disconnect();
    nodesChanged.disconnect();
    nodeIdsChanged.disconnect();
  };
});

@forbidExtraArgs
export default class PrivateNetworkTopology extends Component {
  @arg(instanceOf(PrivateNetwork)) privateNetwork;

  @arg dataCenters;

  @arg createSegmentTask;

  @arg(func) onRoutableChange;

  @tracked collapsed = {};

  @tracked paths = {};

  @tracked packets = {};

  @action
  measureConnectors(container) {
    let snap = (value) => Math.round(value) + 0.5;

    let height = parseFloat(getComputedStyle(container).paddingTop);
    let centerX = snap(container.clientWidth / 2);

    let measured = [...container.querySelectorAll("[data-topology-node]")].map((node) => {
      let x = snap(node.offsetLeft + node.offsetWidth / 2);

      return {
        id: node.getAttribute("data-topology-node"),
        path: `M ${x} ${height} L ${centerX} 0`,
        length: Math.hypot(centerX - x, height),
      };
    });

    this.paths = Object.fromEntries(measured.map(({ id, path }) => [id, path]));

    this.packets = Object.fromEntries(
      measured.map(({ id, path, length }) => {
        let duration = length / PACKET_SPEED;
        let count = Math.max(2, Math.round(length / PACKET_SPACING));

        return [
          id,
          Array.from({ length: count }, (_, index) =>
            htmlSafe(
              `offset-path: path("${path}"); animation-duration: ${duration.toFixed(2)}s; animation-delay: ${((-duration * index) / count).toFixed(2)}s; animation-direction: ${index % 2 ? "reverse" : "normal"}`,
            ),
          ),
        ];
      }),
    );
  }

  @action
  toggleCollapse(segmentId) {
    this.collapsed = { ...this.collapsed, [segmentId]: !this.collapsed[segmentId] };
  }

  load = task(async () => {
    await this.privateNetwork.project?.get("servers");

    return this.privateNetwork.segments;
  });

  <template>
    <div ...attributes>
      <div class="border border-gray-200 rounded-lg px-8 pt-12 pb-16 overflow-x-auto">
        <div class="w-max mx-auto">
          <div class="flex flex-col items-center" data-test-topology-network>
            <span class="px-3 py-2 rounded-md border border-gray-300 bg-white text-black whitespace-nowrap">
              {{@privateNetwork.name}}
            </span>

            <DataLoader class="w-full" @load={{this.load}}>
              <:loading>
                <OverlayLoadingSpinner class="min-h-40" @isLoading={{true}} />
              </:loading>
              <:error>
                <p class="text-sm text-gray-500 text-center" data-test-topology-error>
                  {{t "private-network.topology.error-state"}}
                </p>
              </:error>
              <:default as |segments|>
                <div
                  class="{{styles.children}}
                    pt-20! before:content-none! [&>div]:pt-0! [&>div]:before:content-none! [&>div]:after:content-none!"
                  {{trackLayout this.measureConnectors}}
                >
                  <svg
                    class="absolute top-0 left-0 w-full h-20 pointer-events-none fill-none stroke-gray-300 stroke-1"
                    aria-hidden="true"
                    stroke-linecap="round"
                  >
                    {{#each segments as |segment|}}
                      <path
                        class={{unless segment.routable "[stroke-dasharray:6_10]"}}
                        data-test-topology-connector={{segment.id}}
                        d={{get this.paths segment.id}}
                      />
                      {{#if segment.routable}}
                        {{#each (get this.packets segment.id) as |packet|}}
                          <circle
                            class="animate-travel fill-gray-400 motion-reduce:hidden"
                            data-test-topology-packet={{segment.id}}
                            r="1.5"
                            style={{packet}}
                          />
                        {{/each}}
                      {{/if}}
                    {{/each}}
                    {{#if (canManage)}}
                      <path class="[stroke-dasharray:6_10]" d={{get this.paths "create-segment"}} />
                    {{/if}}
                  </svg>

                  {{#each segments as |segment|}}
                    <div
                      class={{styles.child}}
                      data-test-topology-segment={{segment.id}}
                      data-topology-node={{segment.id}}
                    >
                      <div class="group relative">
                        <button
                          class="flex flex-col items-center px-3 py-2 rounded-md border border-gray-300 bg-white text-sm text-black text-center whitespace-nowrap cursor-pointer hover:border-gray-400"
                          aria-expanded="{{if (get this.collapsed segment.id) 'false' 'true'}}"
                          data-test-topology-segment-toggle={{segment.id}}
                          type="button"
                          {{on "click" (fn this.toggleCollapse segment.id)}}
                        >
                          {{segment.name}}
                          <span class="block text-xs text-gray-500">{{segment.ipv4subnet}}</span>
                          <span class="block text-xs text-gray-500">{{segment.platform}}</span>
                          <span class="block text-xs text-gray-500">{{segment.dataCenter.name}}</span>
                          <span class="block text-xs text-gray-500" data-test-topology-segment-type>
                            {{if
                              segment.routable
                              (t "private-network.segments.type.routable")
                              (t "private-network.segments.type.isolated")
                            }}
                          </span>
                        </button>

                        {{#if (canManage)}}
                          <Dropdown
                            class="absolute -top-2 -right-3 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 w-7 h-6 cursor-pointer flex items-center justify-center rounded-sm bg-white border border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800"
                            aria-label={{t "label.action-menu-aria-label" name=segment.name}}
                            data-test-topology-segment-menu={{segment.id}}
                            @chevron={{false}}
                          >
                            <:header>
                              <IconsMoreHorizontal class="w-8" />
                            </:header>
                            <:default>
                              <DropdownItem
                                {{on
                                  "click"
                                  (modal
                                    "modals/private-network-segment-edit"
                                    segment=segment
                                    task=segment.updateTask
                                    onRoutableChange=this.onRoutableChange
                                  )
                                }}
                              >
                                {{t "label.edit"}}
                              </DropdownItem>
                              <DropdownDestructiveItem
                                {{on
                                  "click"
                                  (modal "modals/organism-delete" entity=segment.name task=segment.deleteTask)
                                }}
                              >
                                {{t "button.delete"}}
                              </DropdownDestructiveItem>
                            </:default>
                          </Dropdown>
                        {{/if}}
                      </div>

                      {{#if (get this.collapsed segment.id)}}
                        <div class={{styles.children}}>
                          <div class={{styles.child}}>
                            <span
                              class="px-3 py-2 rounded-md border border-dashed border-gray-200 text-xs text-gray-500 whitespace-nowrap"
                              data-test-topology-collapsed-summary
                            >
                              {{t "private-network.topology.server-count" count=segment.connectedServerCount}}
                            </span>
                          </div>
                        </div>
                      {{else}}
                        <div class={{styles.children}}>
                          {{#each segment.networkAdapters as |adapter|}}
                            <div class={{styles.child}}>
                              {{#if adapter.server}}
                                <LinkTo
                                  class="px-2 py-1 rounded-sm border border-gray-200 bg-white text-xs text-gray-700 hover:text-gray-900 whitespace-nowrap"
                                  data-test-topology-adapter={{adapter.id}}
                                  @route="server.network-adapters"
                                  @model={{adapter.server.id}}
                                >
                                  {{adapter.name}}
                                </LinkTo>
                                <div class={{styles.children}}>
                                  <div class={{styles.child}}>
                                    <LinkTo
                                      class="px-3 py-2 rounded-md border border-gray-300 bg-white text-sm text-black hover:border-gray-400 text-center whitespace-nowrap"
                                      data-test-topology-server={{adapter.server.id}}
                                      @route="server.details"
                                      @model={{adapter.server.id}}
                                    >
                                      <span class="flex items-center justify-center gap-1.5">
                                        <StatusIndicator
                                          data-test-topology-server-status
                                          @status={{adapter.server.status.statusIndicator}}
                                        />
                                        {{adapter.server.hostname}}
                                      </span>
                                      <span class="block text-xs text-gray-500">{{adapter.server.id}}</span>
                                    </LinkTo>
                                  </div>
                                </div>
                              {{else}}
                                <span
                                  class="px-2 py-1 rounded-sm border border-gray-200 bg-white text-xs text-gray-700 whitespace-nowrap"
                                  data-test-topology-adapter={{adapter.id}}
                                >
                                  {{adapter.name}}
                                </span>
                                <div class={{styles.children}}>
                                  <div class={{styles.child}}>
                                    <span
                                      class="px-3 py-2 rounded-md border border-dashed border-gray-200 text-xs text-gray-500 whitespace-nowrap"
                                      data-test-topology-no-servers
                                    >
                                      {{t "private-network.topology.no-servers"}}
                                    </span>
                                  </div>
                                </div>
                              {{/if}}
                            </div>
                          {{else}}
                            <div class={{styles.child}}>
                              <span
                                class="px-3 py-2 rounded-md border border-dashed border-gray-200 text-xs text-gray-500 whitespace-nowrap"
                                data-test-topology-no-servers
                              >
                                {{t "private-network.topology.no-servers"}}
                              </span>
                            </div>
                          {{/each}}
                        </div>
                      {{/if}}
                    </div>
                  {{else}}
                    <div class={{styles.child}}>
                      <span
                        class="px-3 py-2 rounded-md border border-dashed border-gray-200 text-xs text-gray-500 whitespace-nowrap"
                        data-test-topology-no-segments
                      >
                        {{t "private-network.topology.no-segments"}}
                      </span>
                    </div>
                  {{/each}}

                  {{#if (canManage)}}
                    <div class={{styles.child}} data-test-topology-create-segment data-topology-node="create-segment">
                      <button
                        class="{{styles.dashed}}
                          flex items-center gap-1.5 px-3 py-2 rounded-md text-sm text-gray-500 whitespace-nowrap cursor-pointer hover:text-gray-700"
                        type="button"
                        {{on
                          "click"
                          (modal
                            "modals/private-network-create-segment"
                            privateNetwork=@privateNetwork
                            dataCenters=@dataCenters
                            task=@createSegmentTask
                          )
                        }}
                      >
                        <IconsPlus class="size-3.5" />
                        {{t "button.create-segment"}}
                      </button>
                    </div>
                  {{/if}}
                </div>
              </:default>
            </DataLoader>
          </div>
        </div>
      </div>
    </div>
  </template>
}
